import { TGlobalState } from 'reducers';
import { ServiceSelection } from 'reducers/selectedServices';
import { createSelector } from 'reselect';

import { TAppointment, TProfessional } from 'types/appointment';
import { TPublicUser } from 'types/publicUser';

const getPublicUser = (state: TGlobalState): TPublicUser => state.publicUser;

export const selectSelectedProfessional = (state: TGlobalState) => {
  const appointment = state.appointment as Partial<TAppointment>;

  return appointment?.professional?.guid
    ? appointment.professional
    : state.selectedProfessional;
};

export const hasTeamMembers = createSelector(
  getPublicUser,
  (publicUser) => publicUser.multi_user,
);

export const getAllProfessionals = createSelector(
  getPublicUser,
  (publicUser) => {
    if (publicUser?.users?.length > 0) {
      return publicUser.users.filter(
        (user) =>
          user.available_for_booking && user.assigned_services.length !== 0,
      );
    }

    return [];
  },
);

export const getSelectedProfessional = createSelector(
  getPublicUser,
  selectSelectedProfessional,
  (publicUser, selectedProfessional: TProfessional) => {
    const isMultiUser = publicUser?.multi_user;
    const professionals = publicUser.users || [];

    return isMultiUser ? selectedProfessional : professionals[0];
  },
);

/**
 * Given a list of professionals and a list of selected services, returns a list of professionals who can fulfill all selected services
 *
 * @param professionals The list of professionals
 * @param selectedServices The list of selected services
 * @returns a list of professionals who can fulfill all selected services
 */
export const selectableProfessionals = (
  professionals: TProfessional[] = [],
  selectedServices: ServiceSelection[],
) => {
  if (selectedServices.length === 0) return professionals;

  return professionals.filter((professional) => {
    const professionalServices = new Set<string>();

    for (const service of professional.services) {
      if (!service.options || service.options.length === 0) {
        professionalServices.add(service.guid);
      } else {
        for (const option of service.options || []) {
          professionalServices.add(option.guid);
        }
      }
    }

    return selectedServices.every((service) =>
      professionalServices.has(service.optionId || service.guid),
    );
  });
};

export const getSelectableProfessionals = createSelector(
  getAllProfessionals,
  (state: TGlobalState) => state.selectedServices,
  selectableProfessionals,
);

export const getSelectedOrPotentialServicesProviders = createSelector(
  getSelectedProfessional,
  getSelectableProfessionals,
  (selectedProfessional, selectableProfessionals = []) => {
    // If a professional is selected and can fulfill all selected services, use them.
    const canSelectedProfessionalFulfillServices = selectableProfessionals.some(
      (p: TProfessional) => p.guid === selectedProfessional?.guid,
    );

    if (selectedProfessional && canSelectedProfessionalFulfillServices) {
      return [selectedProfessional];
    }

    // Otherwise, fall back to the professionals that can fulfill the services
    return selectableProfessionals;
  },
);
